import React, { useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export interface InfoCellProps {
  label: string;
  value?: React.ReactNode;
  colSpan?: 1 | 2 | 3 | 4;
  icon?: React.ReactNode;
  copyable?: boolean | string;
  copyTooltip?: string;
  href?: string;
  badge?: React.ReactNode;
  helperText?: string;
  isEditing?: boolean;
  editValue?: string;
  placeholder?: string;
  type?: "text" | "textarea";
  onChange?: (value: string) => void;
  renderEdit?: () => React.ReactNode;
  className?: string;
  valueClassName?: string;
}

export const InfoCell: React.FC<InfoCellProps> = ({
  label,
  value,
  colSpan = 1,
  icon,
  copyable = false,
  copyTooltip = "Sao chép",
  href,
  badge,
  helperText,
  isEditing = false,
  editValue = "",
  placeholder,
  type = "text",
  onChange,
  renderEdit,
  className = "",
  valueClassName = "",
}) => {
  const [copied, setCopied] = useState(false);

  const colSpanClasses = {
    1: "col-span-1",
    2: "col-span-1 sm:col-span-2",
    3: "col-span-1 sm:col-span-2 lg:col-span-3",
    4: "col-span-1 sm:col-span-2 lg:col-span-4",
  }[colSpan];

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const textToCopy =
      typeof copyable === "string"
        ? copyable
        : typeof value === "string" || typeof value === "number"
          ? String(value)
          : editValue || "";

    if (!textToCopy) return;

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success(`Đã sao chép: ${textToCopy}`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`group relative flex flex-col justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/70 hover:bg-slate-50/90 transition-all duration-150 min-h-[68px] ${colSpanClasses} ${className}`}
    >
      {/* Header / Label row */}
      <div className="flex items-center justify-between gap-1.5 mb-1.5">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500/90 select-none">
          {icon && <span className="text-slate-400 shrink-0">{icon}</span>}
          <span>{label}</span>
        </span>

        <div className="flex items-center gap-1">
          {badge && <div className="shrink-0">{badge}</div>}
          {copyable && !isEditing && (
            <button
              type="button"
              onClick={handleCopy}
              title={copyTooltip}
              aria-label={copyTooltip}
              className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors opacity-80 hover:opacity-100 focus:outline-none"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Body / Value or Edit Input */}
      <div className="flex-1 flex flex-col justify-center">
        {isEditing ? (
          renderEdit ? (
            renderEdit()
          ) : type === "textarea" ? (
            <Textarea
              value={editValue}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={placeholder || `Nhập ${label.toLowerCase()}...`}
              rows={colSpan === 4 ? 2 : 1}
              className="min-h-[56px] text-xs sm:text-sm bg-white border-slate-200 focus-visible:ring-indigo-500 resize-none py-1.5 px-2.5"
            />
          ) : (
            <Input
              value={editValue}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={placeholder || `Nhập ${label.toLowerCase()}...`}
              className="h-8 text-xs sm:text-sm bg-white border-slate-200 focus-visible:ring-indigo-500 px-2.5"
            />
          )
        ) : (
          <div className="flex items-center justify-between gap-2 min-w-0">
            {href ? (
              <a
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                className={`text-xs sm:text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1 break-words ${valueClassName}`}
              >
                <span>{value || <span className="text-slate-300 italic font-normal">Chưa thiết lập</span>}</span>
                {href.startsWith("http") && <ExternalLink className="w-3 h-3 shrink-0 opacity-70" />}
              </a>
            ) : (
              <span
                className={`text-xs sm:text-sm font-bold text-slate-800 break-words leading-relaxed ${
                  !value ? "text-slate-300 italic font-normal" : ""
                } ${valueClassName}`}
              >
                {value ?? "Chưa thiết lập"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Helper text */}
      {helperText && (
        <p className="mt-1 text-[10px] text-slate-400 tracking-tight leading-none">
          {helperText}
        </p>
      )}
    </div>
  );
};
export default InfoCell;
